import React from 'react'
import { RouteComponentProps } from '@reach/router'
import useImportDetails from './hooks/useImportDetails'
import useImportFailed, { IBookingImportFailedWithId } from './hooks/useImportFailed'
import Appbar from '../Appbar'
import { omit } from 'lodash'
import {
  IconButton,
  Flex,
  PageContent,
  IGridTableColumn,
  Skeleton,
  Icon,
  Avatar,
  NewGridTable,
} from '@ui'
import { IBookingImportFailed, IBookingImportVersion } from '@api'
import { Position, Tag } from '@blueprintjs/core'
import { Tooltip2 } from '@blueprintjs/popover2'
import { Popover } from '@ui'
import { DATE_PARSE_FORMAT, DateUtil } from '@utils'
import moment from 'moment-timezone'
import track from '@track'
import Event from '@track/Event'

export const getFormattedDate = (date: string, format: string, timezone: string) => {
  return DateUtil.utc(date, timezone).format(format)
}

export default function ImportDetails({ id, navigate }: { id: string } & RouteComponentProps) {
  const { details, onNextPage, status, pageInfo, createdAt, importedBy } = useImportDetails(id)
  const { failedImportBookings } = useImportFailed(id)

  const columns: IGridTableColumn<IBookingImportVersion | IBookingImportFailedWithId>[] = [
    {
      label: 'Status',
      Cell: ({ skeleton, cell }) => {
        if (skeleton || !cell) {
          return <Skeleton />
        }
        if ('errors' in cell?.data) {
          return (
            <Popover
              interactionKind={'hover'}
              content={<FailedReasons failedBooking={cell.data ?? {}} />}>
              <Tag intent="danger" minimal round>
                Failed
              </Tag>
            </Popover>
          )
        }
        return (
          <Tag intent="success" minimal round>
            Created
          </Tag>
        )
      },
    },
    {
      label: 'Date & Time',
      sort: 'startDate',
      Cell: ({ skeleton, cell }) => {
        if (skeleton) return <Skeleton withButton />
        if (!cell?.data?.booking) return null

        const timezone = cell?.data.booking?.space?.floor.building?.timezone ?? moment.tz.guess()

        const startDate = DateUtil.utc(cell?.data?.booking?.startDate, timezone).format(
          'MMM d, yyyy'
        )
        const startTime = DateUtil.utc(cell?.data?.booking?.startDate, timezone).format('hh:mmA')

        const endDate = DateUtil.utc(cell?.data?.booking?.endDate, timezone).format('MMM d, yyyy')
        const endTime = DateUtil.utc(cell?.data?.booking?.endDate, timezone).format('hh:mmA')

        return (
          <Tooltip2 content={moment(timezone).zoneAbbr()}>
            <div
              // className="ml-2 font-medium text-base hover:underline hover:text-link cursor-pointer flex flex-col"
              className="flex flex-row items-center"
              onClick={() => {
                if (cell && cell?.data.id)
                  navigate?.(`${window.location.pathname}?bookingId=${cell?.data.id}`)
                track.bookings({ eventName: Event.ROW_CLICK })
              }}>
              <div className="flex flex-col mr-3">
                <div className="font-medium">{startDate}</div>
                <div className="font-light">{startTime}</div>
              </div>
              <Icon name="arrow-right" className="mr-3" />
              <div className="flex flex-col">
                <div className="font-medium">{endDate}</div>
                <div className="font-light">{endTime}</div>
              </div>
            </div>
          </Tooltip2>
        )
      },
    },
    {
      label: 'Location',
      Cell: ({ skeleton, cell }) => {
        if (skeleton) return <Skeleton />
        if (!cell) return null
        return (
          <div className="flex flex-col">
            <div className="font-medium">
              {cell?.data.booking?.space?.floor.name} -{' '}
              {cell?.data.booking?.space?.floor.building?.name}
            </div>
            <div className="font-light">{cell?.data.booking?.space?.floor.building?.city}</div>
          </div>
        )
      },
    },
    {
      label: 'Space',
      sort: 'space',
      Cell: ({ skeleton, cell }) => {
        if (skeleton) return <Skeleton />
        if (!cell) return null
        return cell?.data.booking?.space ? (
          <Tag round intent="primary">
            {cell?.data.booking?.space?.externalId}
          </Tag>
        ) : (
          <span>N/A</span>
        )
      },
    },
    {
      label: 'Booked for',
      sort: 'bookedFor',
      Cell: ({ skeleton, cell }) => {
        if (skeleton) return <Skeleton />
        if (!cell) return null
        return (
          <div className="flex items-center">
            <div className="flex items-center mr-3">
              <Avatar
                name={cell?.data.booking?.employee?.name}
                assets={cell?.data.booking?.employee?.assets}
                size={Avatar.Size.SM}
              />
              <div
                className="ml-2 pt-1 cursor-pointer"
                onClick={() => navigate?.(`?employeeId=${cell?.data.booking?.employee?.id}`, {})}>
                <div className="font-bold hover:underline">
                  {cell?.data.booking?.employee?.name}
                </div>
                <div className="font-light">{cell?.data.booking?.employee?.title}</div>
              </div>
            </div>
          </div>
        )
      },
    },
    {
      label: 'Booked by',
      sort: 'bookedBy',
      Cell: ({ skeleton, cell }) => {
        if (skeleton) return <Skeleton />
        if (!cell) return null
        return (
          <div className="flex items-center">
            <div className="flex items-center mr-3">
              <Avatar
                name={cell?.data.booking?.bookedBy?.employee?.name}
                assets={cell?.data.booking?.bookedBy?.employee?.assets}
                size={Avatar.Size.SM}
              />
              <div
                className="ml-2 pt-1 cursor-pointer"
                onClick={() =>
                  navigate?.(`?employeeId=${cell?.data.booking?.bookedBy?.employee?.id}`)
                }>
                <div className="font-bold hover:underline">
                  {cell?.data.booking?.bookedBy?.employee?.name}
                </div>
                <div className="font-light">{cell?.data.booking?.bookedBy?.employee?.title}</div>
              </div>
            </div>
          </div>
        )
      },
    },
  ]

  return (
    <>
      <Appbar
        title={
          <Flex align="center" justify="center">
            <span style={{ marginRight: 8 }}>
              <Tooltip2 content="Back to Import History" placement={Position.TOP}>
                <IconButton
                  onClick={() => navigate?.('/bookings/history')}
                  iconName="chevron-left"
                  minimal
                />
              </Tooltip2>
            </span>
            <Flex direction="column">
              <span style={{ fontSize: 14 }}>Import Details</span>
              <span
                style={{
                  fontSize: 12,
                  whiteSpace: 'pre-wrap',
                  color: 'var(--grey1)',
                }}>
                {createdAt &&
                  `${DateUtil(createdAt).format('MMM d, yyyy @h:mma')} by ${importedBy}`}
              </span>
            </Flex>
          </Flex>
        }
      />
      <PageContent>
        <NewGridTable<IBookingImportVersion | IBookingImportFailedWithId>
          status={status}
          // there is some difficult validation stuff going on here that
          // needs to be resolved before you can remove the ignore
          /* eslint-disable @typescript-eslint/ban-ts-comment */
          // @ts-ignore
          data={[...failedImportBookings, ...details]}
          columns={columns}
          page={{
            hasNextPage: pageInfo?.hasNextPage || false,
            endCursor: pageInfo?.endCursor || '',
            onNextPage,
          }}
        />
      </PageContent>
    </>
  )
}

function FailedReasons({ failedBooking }: { failedBooking: IBookingImportFailed }) {
  if (
    failedBooking.errors === null ||
    Object.values(omit(failedBooking.errors, '__typename')).every((value) => value === null)
  ) {
    return (
      <div
        style={{
          maxWidth: 350,
          padding: 20,
          zIndex: 100,
        }}>
        Unknown Error
      </div>
    )
  }

  return (
    <Flex direction="column" style={{ padding: 20 }}>
      {Object.entries(failedBooking.errors).map(([key, value]) => {
        if (!value || key === '__typename') return null
        return <span key={key}>{value}</span>
      })}
    </Flex>
  )
}
